import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

// Cores e estilos premium do tema do sistema (navy escuro, emerald, cinza)
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: '#1e3a8a',
    paddingBottom: 15,
    marginBottom: 20,
  },
  headerLeft: {
    flexDirection: 'column',
    maxWidth: '70%',
  },
  headerRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#64748b',
  },
  metaText: {
    fontSize: 9,
    color: '#64748b',
    marginTop: 2,
  },
  badgeReport: {
    backgroundColor: '#1e3a8a',
    color: '#ffffff',
    fontSize: 9,
    fontWeight: 'bold',
    padding: '4px 8px',
    borderRadius: 4,
    textTransform: 'uppercase',
  },
  
  // Dashboard Cards
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 10,
  },
  statCard: {
    flex: 1,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 6,
    padding: 10,
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 8,
    color: '#64748b',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0f172a',
  },

  // Tabela
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0f172a',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    minHeight: 22,
    alignItems: 'center',
  },
  tableRowEven: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    minHeight: 22,
    alignItems: 'center',
  },
  
  // Colunas da Tabela
  th: {
    color: '#ffffff',
    fontSize: 8,
    fontWeight: 'bold',
    padding: '3px 4px',
  },
  td: {
    fontSize: 7.5,
    color: '#334155',
    padding: '3px 4px',
  },
  
  // Tamanhos das colunas (totalizando 100% de largura)
  colData: { width: '9%' },
  colNome: { width: '30%' },
  colCpf: { width: '11%' },
  colContato: { width: '22%' },
  colLote: { width: '10%' },
  colValor: { width: '7%' },
  colStatus: { width: '11%', alignItems: 'center' },

  // Elementos internos de linha
  participantName: {
    fontWeight: 'bold',
    color: '#0f172a',
    fontSize: 9,
  },
  customFieldsContainer: {
    marginTop: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  customFieldText: {
    fontSize: 7,
    color: '#475569',
    backgroundColor: '#e2e8f0',
    padding: '1px 3px',
    borderRadius: 2,
    marginRight: 4,
  },
  cpfText: {
    fontFamily: 'Courier',
    fontSize: 8.5,
    color: '#475569',
  },
  contactText: {
    fontSize: 8,
    color: '#475569',
  },
  priceText: {
    fontWeight: 'bold',
    color: '#1e3a8a',
  },

  // Status Badges no PDF
  statusBadge: {
    fontSize: 7.5,
    fontWeight: 'bold',
    paddingTop: 4,
    paddingBottom: 3,
    paddingHorizontal: 6,
    borderRadius: 4,
    textAlign: 'center',
    textTransform: 'uppercase',
    lineHeight: 1.1,
  },
  statusApproved: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
  },
  statusPending: {
    backgroundColor: '#fef9c3',
    color: '#a16207',
  },
  statusRejected: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
  },

  // Rodapé
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7.5,
    color: '#94a3b8',
  }
});

// Componente do Documento PDF
export default function InscritosPDF({ evento, inscricoes }) {
  const dataGeracao = new Date().toLocaleString('pt-BR');

  // Cálculos das estatísticas para os cards
  const totalInscritos = inscricoes.length;
  
  const totalAprovados = inscricoes.filter(insc => {
    const status = String(insc.status || '').toLowerCase();
    return status === 'approved';
  }).length;

  const totalCredenciados = inscricoes.filter(insc => {
    const status = String(insc.status || '').toLowerCase();
    return status === 'credenciado';
  }).length;

  const totalPendentes = inscricoes.filter(insc => {
    const status = String(insc.status || '').toLowerCase();
    return status === 'pending';
  }).length;

  const receitaTotal = inscricoes
    .filter(insc => {
      const status = String(insc.status || '').toLowerCase();
      return status === 'approved' || status === 'credenciado';
    })
    .reduce((acc, insc) => acc + Number(insc.valor_pago || 0), 0);

  return (
    <Document title={`Inscritos - ${evento?.titulo || 'Evento'}`}>
      <Page size="A4" orientation="landscape" style={styles.page}>
        
        {/* Cabeçalho */}
        <View style={styles.headerContainer}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>{evento?.titulo || 'Relatório de Inscrições'}</Text>
            <Text style={styles.subtitle}>
              Evento ID: {evento?.id} • Data do Evento: {evento?.data_evento ? new Date(evento.data_evento).toLocaleDateString('pt-BR') : 'N/A'}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.badgeReport}>Relatório de Inscritos</Text>
            <Text style={styles.metaText}>Gerado em: {dataGeracao}</Text>
          </View>
        </View>

        {/* Estatísticas Rápidas */}
        <View style={styles.statsContainer}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Inscritos</Text>
            <Text style={styles.statValue}>{totalInscritos}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Aprovados</Text>
            <Text style={styles.statValue}>{totalAprovados}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Credenciados</Text>
            <Text style={styles.statValue}>{totalCredenciados}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Pendentes</Text>
            <Text style={styles.statValue}>{totalPendentes}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Receita Confirmada</Text>
            <Text style={styles.statValue}>{receitaTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</Text>
          </View>
        </View>

        {/* Tabela de Participantes */}
        <View style={styles.table}>
          {/* Cabeçalho da Tabela */}
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colData]}>Data</Text>
            <Text style={[styles.th, styles.colNome]}>Nome / Info Adicional</Text>
            <Text style={[styles.th, styles.colCpf]}>CPF</Text>
            <Text style={[styles.th, styles.colContato]}>Contato</Text>
            <Text style={[styles.th, styles.colLote]}>Lote</Text>
            <Text style={[styles.th, styles.colValor]}>Valor</Text>
            <Text style={[styles.th, styles.colStatus]}>Status</Text>
          </View>

          {/* Linhas da Tabela */}
          {inscricoes.map((insc, index) => {
            // Zebrado alternando linhas
            const rowStyle = index % 2 === 0 ? styles.tableRow : styles.tableRowEven;
            
            // Tratamento de respostas de campos adicionais
            let respostas = {};
            try {
              if (insc.respostas) {
                respostas = typeof insc.respostas === 'string' ? JSON.parse(insc.respostas) : insc.respostas;
              }
            } catch (e) {}

            // Formatação do status
            const statusLower = String(insc.status || '').toLowerCase();
            let statusLabel = statusLower;
            let statusBadgeStyle = [styles.statusBadge, styles.statusPending];

            if (statusLower === 'approved') {
              statusLabel = 'Aprovado';
              statusBadgeStyle = [styles.statusBadge, styles.statusApproved];
            } else if (statusLower === 'credenciado') {
              statusLabel = 'Credenciado';
              statusBadgeStyle = [styles.statusBadge, styles.statusApproved];
            } else if (statusLower === 'pending') {
              statusLabel = 'Pendente';
              statusBadgeStyle = [styles.statusBadge, styles.statusPending];
            } else if (statusLower === 'rejected') {
              statusLabel = 'Recusado';
              statusBadgeStyle = [styles.statusBadge, styles.statusRejected];
            } else if (statusLower === 'refunded') {
              statusLabel = 'Reembolsado';
              statusBadgeStyle = [styles.statusBadge, styles.statusRejected];
            } else if (statusLower === 'cancelled') {
              statusLabel = 'Cancelado';
              statusBadgeStyle = [styles.statusBadge, styles.statusRejected];
            }

            return (
              <View style={rowStyle} key={insc.id || index} wrap={false}>
                {/* Data */}
                <Text style={[styles.td, styles.colData]}>
                  {insc.data_inscricao ? new Date(insc.data_inscricao).toLocaleDateString('pt-BR') : '-'}
                </Text>

                {/* Nome e Respostas Adicionais */}
                <View style={[styles.td, styles.colNome]}>
                  <Text style={styles.participantName}>{insc.nome || '-'}</Text>
                  {Object.keys(respostas).length > 0 && (
                    <View style={styles.customFieldsContainer}>
                      {Object.entries(respostas).map(([key, val]) => (
                        <Text key={key} style={styles.customFieldText}>
                          {key}: {String(val)}
                        </Text>
                      ))}
                    </View>
                  )}
                </View>

                {/* CPF */}
                <Text style={[styles.td, styles.colCpf, styles.cpfText]}>
                  {insc.cpf || '-'}
                </Text>

                {/* Contato */}
                <View style={[styles.td, styles.colContato]}>
                  <Text style={styles.contactText}>{insc.email || '-'}</Text>
                  <Text style={styles.contactText}>{insc.telefone || '-'}</Text>
                </View>

                {/* Lote */}
                <Text style={[styles.td, styles.colLote]}>
                  {insc.lote_nome || '-'}
                </Text>

                {/* Valor */}
                <Text style={[styles.td, styles.colValor, styles.priceText]}>
                  {Number(insc.valor_pago || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Text>

                {/* Status */}
                <View style={[styles.td, styles.colStatus]}>
                  <Text style={statusBadgeStyle}>{statusLabel}</Text>
                </View>
              </View>
            );
          })}

          {inscricoes.length === 0 && (
            <View style={styles.tableRow} justify="center" align="center">
              <Text style={[styles.td, { width: '100%', textAlign: 'center', color: '#64748b' }]}>
                Nenhuma inscrição encontrada para este evento.
              </Text>
            </View>
          )}
        </View>

        {/* Rodapé Dinâmico */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            Relatório de Inscritos • {evento?.titulo || 'Evento'}
          </Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => (
            `Página ${pageNumber} de ${totalPages}`
          )} />
        </View>

      </Page>
    </Document>
  );
}
